import authService from './authService';
import config from '../config';

const TTS_API_PREFIX = '/api/v1/tts';

/**
 * Sends text to the backend to initiate speech conversion asynchronously.
 * The backend returns a hash immediately and processes the audio in the background.
 *
 * @param {string} text - The text content to convert to speech.
 * @param {string|null} [voice=null] - The optional voice ID to use.
 * @returns {Promise<Object>} - An object containing the text hash (TTSAsyncResponse).
 * @throws {Error} If the API request fails.
 */
export const generateSpeech = async (text, voice = null) => {
  if (!text || typeof text !== 'string' || !text.trim()) {
    throw new Error('Text to synthesize cannot be empty.');
  }
  const url = `${config.API_BASE_URL}${TTS_API_PREFIX}/synthesize-async`;
  console.log(`[ttsApi] Requesting ASYNC speech synthesis for text: "${text.substring(0, 50)}..." with voice: ${voice || 'default'}`);
  const payload = { text, voice };
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to initiate speech synthesis: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
      }
      throw new Error(errorDetail);
    }
    const result = await response.json();
    console.log('[ttsApi] Speech synthesis initiated successfully. Received hash:', result);
    return result;
  } catch (error) {
    console.error('[ttsApi] Error initiating speech synthesis:', error);
    authService.trackNetworkError('generateSpeechAsync', error);
    throw error;
  }
};

/**
 * Sends text to the backend to be converted into speech audio synchronously.
 * The backend will wait for the process to complete and return the file resource details.
 *
 * @param {string} text - The text content to convert to speech.
 * @param {string|null} [voice=null] - The optional voice ID to use.
 * @returns {Promise<Object>} - The file metadata object for the created audio resource (TSSSyncResponse).
 * @throws {Error} If the API request.
 */
export const generateSpeechSync = async (text, voice = null) => {
  if (!text || typeof text !== 'string' || !text.trim()) {
    throw new Error('Text to synthesize cannot be empty.');
  }
  const url = `${config.API_BASE_URL}${TTS_API_PREFIX}/synthesize`;
  console.log(`[ttsApi] Requesting SYNC speech synthesis for text: "${text.substring(0, 50)}..." with voice: ${voice || 'default'}`);
  const payload = { text, voice };
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to generate speech: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
      }
      throw new Error(errorDetail);
    }
    const result = await response.json();
    console.log('[ttsApi] Speech synthesis successful (sync). Received file metadata:', result);
    return result;
  } catch (error) {
    console.error('[ttsApi] Error generating speech (sync):', error);
    authService.trackNetworkError('generateSpeechSync', error);
    throw error;
  }
};

/**
 * Fetches the list of available TTS voices from the backend.
 * @returns {Promise<Object>} An object containing the list of voices and the default voice ID.
 * @throws {Error} If the API request fails.
 */
export const getAvailableVoices = async () => {
  const url = `${config.API_BASE_URL}${TTS_API_PREFIX}/voices`;
  console.log(`[ttsApi] Fetching available voices from: ${url}`);
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch available voices: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
      }
      throw new Error(errorDetail);
    }
    const result = await response.json();
    console.log('[ttsApi] Fetched available voices:', result);
    return result;
  } catch (error) {
    console.error('[ttsApi] Error fetching available voices:', error);
    throw error;
  }
};

export default {
  generateSpeech,
  generateSpeechSync,
  getAvailableVoices,
};